import { threadsUrls } from '@/util/links'
import { createFrameParser } from '@/util/sse_frames'
import type { ObjectiveLevel } from '../objectives/objective_levels'

export type GenerationTargetType = 'goal' | 'objective'

export interface GenerationPayload {
  saved_page_uuid: string
  goal_uuid: string
  target_type: GenerationTargetType
  target_level?: ObjectiveLevel
  target_text: string
  goal_text: string
  grade: string
  teacher_directions?: string | null
}

export interface GenerationPhase {
  type: string
  data: Record<string, unknown>
}

export interface GenerationRun extends Promise<string | null> {
  /** Stops reading the stream. The server still finishes and saves the run. */
  close: () => void
}

const SAVED_EVENT = 'assessment_saved'
const ERROR_EVENT = 'error'
// Failed images or printable resources don't cost the run: the server saves the assessment
// with placeholders and then sends assessment_saved, so these errors are not terminal.
const NON_FATAL_ERROR_PHASES = ['asset_generation', 'supplemental_generation']

/** The route refused the request; 404 also means the requester may not generate on this page. */
export class GenerationRequestError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`Generation request failed: HTTP ${status}`)
    this.name = 'GenerationRequestError'
    this.status = status
  }
}

/**
 * The stream ended without a verdict. The server drains and saves a run whose client went
 * away, so the outcome is unknown rather than failed.
 */
export class GenerationIncompleteError extends Error {
  constructor() {
    super('The connection ended before the assessment was saved')
    this.name = 'GenerationIncompleteError'
  }
}

/** The server answered and reported the run failed. */
export class GenerationFailedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GenerationFailedError'
  }
}

interface StreamVerdict {
  isSaved: boolean
  savedUuid: string | null
  fatalError: Error | null
}

function parseMessage(frame: string): GenerationPhase | null {
  try {
    const message = JSON.parse(frame)
    return { type: message?.type, data: message?.data ?? {} }
  } catch {
    // One unparseable frame is not worth failing a two-minute run over.
    console.error('Unparseable generation event', frame)
    return null
  }
}

function recordMessage(verdict: StreamVerdict, message: GenerationPhase, onPhase: (phase: GenerationPhase) => void) {
  if (message.type === SAVED_EVENT) {
    // The record exists once this arrives, even without an id to link to.
    verdict.isSaved = true
    verdict.savedUuid = typeof message.data.id === 'string' ? message.data.id : null
    return
  }
  if (message.type === ERROR_EVENT) {
    if (NON_FATAL_ERROR_PHASES.includes(message.data.phase as string)) return
    const serverMessage = typeof message.data.message === 'string' ? message.data.message : ''
    verdict.fatalError = new GenerationFailedError(serverMessage || 'The assessment could not be generated')
    return
  }
  onPhase(message)
}

async function readVerdict(response: Response, signal: AbortSignal, onPhase: (phase: GenerationPhase) => void): Promise<StreamVerdict> {
  const reader = (response.body as ReadableStream<Uint8Array>).getReader()
  const decoder = new TextDecoder()
  const parser = createFrameParser()
  const verdict: StreamVerdict = { isSaved: false, savedUuid: null, fatalError: null }
  const isDecided = () => verdict.isSaved || verdict.fatalError !== null
  const handleFrames = (frames: string[]) => {
    frames.map(parseMessage).forEach((message) => message && recordMessage(verdict, message, onPhase))
  }

  try {
    while (!isDecided()) {
      let chunk: ReadableStreamReadResult<Uint8Array>
      try {
        chunk = await reader.read()
      } catch (readError) {
        // Our own abort stays a plain rejection; a dropped connection is an unknown outcome.
        if (signal.aborted) throw readError
        break
      }
      if (chunk.done) break
      handleFrames(parser.push(decoder.decode(chunk.value, { stream: true })))
    }
    if (!isDecided()) handleFrames(parser.flush())
  } finally {
    reader.cancel().catch(() => {})
  }
  return verdict
}

/**
 * Posts to the Toolkit generation route and reads its event stream, calling `onPhase` with
 * each progress event. Resolves with the saved assessment's uuid (null if the server sent
 * none) or rejects with GenerationRequestError, GenerationIncompleteError or GenerationFailedError.
 * fetch rather than EventSource, because the route takes a POST body.
 */
export function streamToolkitGeneration(payload: GenerationPayload, onPhase: (phase: GenerationPhase) => void): GenerationRun {
  const controller = new AbortController()

  const finished = (async () => {
    const response = await fetch(`${threadsUrls.api}/assessments/generate/toolkit`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal
    })
    if (!response.ok || !response.body) {
      throw new GenerationRequestError(response.status)
    }
    const { isSaved, savedUuid, fatalError } = await readVerdict(response, controller.signal, onPhase)
    if (fatalError) throw fatalError
    if (!isSaved) throw new GenerationIncompleteError()
    return savedUuid
  })()

  return Object.assign(finished, { close: () => controller.abort() })
}
